import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faSparkles } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { classNames } from "@/utils/classNames";

interface AiGeneratedBadgeProps {
	/** Rendered only when true, so a caller can pass the flag straight through. */
	isGenerated?: boolean | null;
	/** `full` adds the explanatory sentence; `compact` is the badge alone. */
	variant?: "full" | "compact";
	className?: string;
}

/**
 * "KI-generiert" — the transparency marker on media a trainee is shown.
 *
 * Deliberately not a subtle corner mark. The EU AI Act obligation this answers is about
 * someone who is *not* looking for it noticing that what they are watching was generated,
 * so it sits above the player at body size with an icon, in the amber that already means
 * "read this" elsewhere in the product.
 *
 * Driven by `course-file.aiGenerated` and nothing else. Inferring it from a join through
 * the media tables would answer "no" for phase TTS audio, which is generated, and would
 * stop answering at all once the retention job clears the job away.
 */
export const AiGeneratedBadge: FunctionComponent<AiGeneratedBadgeProps> = ({
	isGenerated,
	variant = "full",
	className,
}) => {
	const { t } = useTranslation();

	if (!isGenerated) {
		return null;
	}

	if (variant === "compact") {
		return (
			<span
				className={classNames(
					"inline-flex w-fit items-center gap-1.5 rounded-full bg-status-running-light px-2.5 py-1 text-xs font-semibold text-warning-ink",
					className
				)}
			>
				<Icon icon={faSparkles} />
				{t("aiGenerated.badge")}
			</span>
		);
	}

	return (
		<div
			className={classNames(
				"flex flex-wrap items-center gap-x-3 gap-y-1 rounded-card bg-status-running-light px-4 py-2.5",
				className
			)}
		>
			<span className="inline-flex items-center gap-1.5 text-sm font-bold text-warning-ink">
				<Icon icon={faSparkles} />
				{t("aiGenerated.badge")}
			</span>
			<span className="text-xs text-warning-ink">{t("aiGenerated.description")}</span>
		</div>
	);
};
